/**
 * Gestión del tema visual (claro / oscuro / auto).
 *
 * - Persiste la preferencia en localStorage (`theme-pref`).
 * - Resuelve `auto` consultando `prefers-color-scheme` y se suscribe a
 *   cambios del sistema en tiempo real.
 * - Aplica `data-theme="light|dark"` en <html> y actualiza la meta
 *   theme-color (status bar móvil) para que coincida con el AppHeader.
 *
 * El bootstrap inicial vive en index.html (anti-FOUC). Este módulo asume
 * que `data-theme` ya está aplicado al cargar la app y solo gestiona
 * cambios posteriores y la suscripción al sistema.
 */

export type ThemePref = 'light' | 'dark' | 'auto';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme-pref';
const THEME_COLOR_LIGHT = '#187236';
const THEME_COLOR_DARK = '#1f7a3a';

type Listener = (effective: EffectiveTheme, pref: ThemePref) => void;
const listeners = new Set<Listener>();

let mediaQuery: MediaQueryList | null = null;
let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'auto') return v;
  } catch { /* ignore */ }
  // Por defecto CLARO (no 'auto'): la primera vez que se entra, la app está en
  // modo claro; luego el usuario puede cambiarlo en Ajustes y persiste.
  return 'light';
}

function resolveEffective(pref: ThemePref): EffectiveTheme {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  const sysDark = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return sysDark ? 'dark' : 'light';
}

function applyToDom(effective: EffectiveTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', effective);
  // El keyword `only` es la señal más fuerte para que Chrome Android no
  // aplique force-dark a la versión clara (o al revés). Con `light`/`dark`
  // a secas, algunas builds de Chrome aún invierten la página.
  const cs = effective === 'dark' ? 'only dark' : 'only light';
  root.style.colorScheme = cs;
  const csMeta = document.querySelector('meta[name="color-scheme"]');
  if (csMeta) csMeta.setAttribute('content', cs);
  const tcMeta = document.querySelector('meta[name="theme-color"]');
  if (tcMeta) {
    tcMeta.setAttribute('content', effective === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT);
  }
}

function ensureSystemListener(pref: ThemePref): void {
  // Solo cuando pref==='auto' interesa escuchar al sistema. En 'light'/'dark'
  // la preferencia es explícita y los cambios del sistema no deben afectar.
  if (!('matchMedia' in window)) return;
  if (pref === 'auto') {
    if (!mediaQuery) mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaListener) {
      mediaListener = () => {
        const current = readPref();
        if (current !== 'auto') return; // por si cambió la pref entre tanto
        const eff = resolveEffective('auto');
        applyToDom(eff);
        listeners.forEach((l) => l(eff, 'auto'));
      };
      mediaQuery.addEventListener('change', mediaListener);
    }
  } else if (mediaQuery && mediaListener) {
    mediaQuery.removeEventListener('change', mediaListener);
    mediaListener = null;
  }
}

export function getThemePref(): ThemePref {
  return readPref();
}

export function getEffectiveTheme(): EffectiveTheme {
  return resolveEffective(readPref());
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch { /* ignore */ }
  const eff = resolveEffective(pref);
  applyToDom(eff);
  ensureSystemListener(pref);
  listeners.forEach((l) => l(eff, pref));
}

export function subscribeTheme(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Fuerza el tema CLARO sin tocar la preferencia guardada. Lo usa la pantalla de
 * login, que siempre se muestra en claro aunque el usuario tenga el tema oscuro.
 * Desengancha el listener del sistema mientras dura (se re-engancha al
 * restaurar). NO notifica a los subscribers ni escribe en localStorage: es un
 * override puramente visual y temporal.
 */
export function forceLightTheme(): void {
  if (mediaQuery && mediaListener) {
    mediaQuery.removeEventListener('change', mediaListener);
    mediaListener = null;
  }
  applyToDom('light');
}

/**
 * Restaura el tema efectivo desde la preferencia guardada. Lo usa el login al
 * desmontarse (tras iniciar sesión) para devolver el tema del usuario.
 */
export function restoreThemeFromPref(): void {
  const pref = readPref();
  applyToDom(resolveEffective(pref));
  ensureSystemListener(pref);
}

/**
 * Inicializa el listener del sistema según la preferencia guardada.
 * Llamar una vez al arrancar la app (la aplicación al DOM ya la hizo el
 * script inline de index.html — aquí solo nos enganchamos a cambios).
 */
export function initTheme(): void {
  ensureSystemListener(readPref());
}
